import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Button,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Popover,
  TablePagination,
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  PeopleAltOutlined,
  PersonOutline,
  PersonAddOutlined,
  AssignmentOutlined,
  AssignmentTurnedInOutlined,
  ScienceOutlined,
  CheckCircleOutline,
  KeyboardArrowRight,
  Refresh,
} from "@mui/icons-material";
import { lisDashboardService, labOrderService } from "../../services/lis";

// 1. Compact Stat Card component with subtle icon and bold metric
function StatCard({ title, subtitle, value, icon, color, bgColor, isDark, onClick }) {
  const cardBg = isDark ? "#1E1E1E" : "#FFFFFF";
  const borderColor = isDark ? "rgba(255,255,255,0.08)" : "#E2E8F0";
  const textTitleColor = isDark ? "#CBD5E1" : "#475569";
  const textSubtitleColor = isDark ? "#94A3B8" : "#94A3B8";

  return (
    <Paper
      onClick={onClick}
      elevation={0}
      sx={{
        py: 0.6,
        px: 1.2,
        borderRadius: "8px",
        bgcolor: cardBg,
        border: `1px solid ${borderColor}`,
        boxShadow: "none",
        display: "flex",
        alignItems: "center",
        gap: 1.2,
        cursor: onClick ? "pointer" : "default",
        transition: "all 0.15s ease",
        "&:hover": onClick
          ? {
              transform: "translateY(-1px)",
              borderColor: color,
              boxShadow: isDark
                ? "0 2px 8px rgba(0,0,0,0.35)"
                : "0 2px 8px rgba(15, 23, 42, 0.05)",
            }
          : {},
      }}
    >
      {/* Icon Container */}
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: "6px",
          bgcolor: isDark ? `${color}25` : bgColor,
          color: color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {React.cloneElement(icon, { sx: { fontSize: 16 } })}
      </Box>

      {/* Content */}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 0.5 }}>
          <Typography
            sx={{
              fontFamily: "'Satoshi', sans-serif",
              fontSize: "0.72rem",
              fontWeight: 600,
              color: textTitleColor,
              lineHeight: 1.1,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Satoshi', sans-serif",
              fontSize: "1.15rem",
              fontWeight: 800,
              color: color,
              lineHeight: 1,
            }}
          >
            {value ?? "—"}
          </Typography>
        </Box>
        <Typography
          sx={{
            fontFamily: "'Satoshi', sans-serif",
            fontSize: "0.62rem",
            color: textSubtitleColor,
            fontWeight: 400,
            lineHeight: 1,
            mt: 0.2,
          }}
        >
          {subtitle}
        </Typography>
      </Box>
    </Paper>
  );
}

// 2. Compact Quick Action Card component
function QuickActionCard({ title, subtitle, icon, borderColor, color, hoverBg, isDark, onClick }) {
  const cardBg = isDark ? "#1E1E1E" : "#FFFFFF";
  const borderCol = isDark ? "rgba(255,255,255,0.08)" : "#E2E8F0";

  return (
    <Paper
      onClick={onClick}
      elevation={0}
      sx={{
        py: 0.6,
        px: 1.2,
        borderRadius: "8px",
        bgcolor: cardBg,
        border: `1px solid ${borderCol}`,
        cursor: "pointer",
        transition: "all 0.15s ease-in-out",
        display: "flex",
        alignItems: "center",
        gap: 1.2,
        boxShadow: "none",
        "&:hover": {
          borderColor: color || borderColor,
          bgcolor: isDark ? "rgba(255,255,255,0.04)" : hoverBg,
          transform: "translateY(-1px)",
          boxShadow: isDark
            ? "0 2px 8px rgba(0,0,0,0.3)"
            : "0 2px 8px rgba(0,0,0,0.04)",
        },
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: "6px",
          bgcolor: isDark ? `${color}25` : hoverBg,
          color: color || borderColor,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {React.cloneElement(icon, { sx: { fontSize: 16, color: color || borderColor } })}
      </Box>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            fontFamily: "'Satoshi', sans-serif",
            fontSize: "0.78rem",
            fontWeight: 700,
            color: isDark ? "#F8FAFC" : "#0F172A",
            lineHeight: 1.15,
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            fontFamily: "'Satoshi', sans-serif",
            fontSize: "0.64rem",
            color: isDark ? "#94A3B8" : "#64748B",
            fontWeight: 400,
            lineHeight: 1,
            mt: 0.1,
          }}
        >
          {subtitle}
        </Typography>
      </Box>
    </Paper>
  );
}

// 3. Compact Status Badge Pill matching RIS / SamplesQueue
function StatusBadge({ status, isDark }) {
  const statusStyles = {
    Ordered: {
      color: "#2563EB",
      bg: isDark ? "rgba(37, 99, 235, 0.15)" : "#EFF6FF",
      border: isDark ? "rgba(37, 99, 235, 0.3)" : "#DBEAFE",
    },
    "Sample Collected": {
      color: "#EA580C",
      bg: isDark ? "rgba(234, 88, 12, 0.15)" : "#FFF7ED",
      border: isDark ? "rgba(234, 88, 12, 0.3)" : "#FFEDD5",
    },
    "In Progress": {
      color: "#EA580C",
      bg: isDark ? "rgba(234, 88, 12, 0.15)" : "#FFF7ED",
      border: isDark ? "rgba(234, 88, 12, 0.3)" : "#FFEDD5",
    },
    "Result Pending": {
      color: isDark ? "#FBBF24" : "#D97706",
      bg: isDark ? "rgba(217, 119, 6, 0.15)" : "#FEF3C7",
      border: isDark ? "rgba(217, 119, 6, 0.3)" : "#FDE68A",
    },
    Completed: {
      color: "#16A34A",
      bg: isDark ? "rgba(22, 163, 74, 0.15)" : "#F0FDF4",
      border: isDark ? "rgba(22, 163, 74, 0.3)" : "#DCFCE7",
    },
    Cancelled: {
      color: "#DC2626",
      bg: isDark ? "rgba(220, 38, 38, 0.15)" : "#FEF2F2",
      border: isDark ? "rgba(220, 38, 38, 0.3)" : "#FEE2E2",
    },
  };

  const style = statusStyles[status] || {
    color: "#64748B",
    bg: isDark ? "rgba(100, 116, 139, 0.15)" : "#F1F5F9",
    border: isDark ? "rgba(100, 116, 139, 0.3)" : "#E2E8F0",
  };

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 0.9,
        py: 0.15,
        borderRadius: "5px",
        bgcolor: style.bg,
        border: `1px solid ${style.border}`,
        color: style.color,
        fontFamily: "'Satoshi', sans-serif",
        fontSize: "0.66rem",
        fontWeight: 700,
        whiteSpace: "nowrap",
        lineHeight: 1.2,
      }}
    >
      {status}
    </Box>
  );
}

export default function LabDashboardHome({
  isDark,
  onRegisterPatient,
  onCreateOrder,
  onCollectSample,
  onViewReport,
  onEnterResult,
  onNavigateToSamples,
  onNavigateToResults,
}) {
  const [stats, setStats] = useState({
    patientsToday: 0,
    ordersToday: 0,
    samplesCollected: 0,
    resultsCompleted: 0,
  });
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const cardBg = isDark ? "#1E1E1E" : "#FFFFFF";
  const borderColor = isDark ? "rgba(255,255,255,0.08)" : "#E2E8F0";
  const textPrimary = isDark ? "#F8FAFC" : "#0F172A";
  const textSecondary = isDark ? "#94A3B8" : "#64748B";

  // Fetch real data from backend (requesting 50 to allow high-density view)
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, samplesRes] = await Promise.all([
        lisDashboardService.getStats().catch(() => null),
        lisDashboardService.getTodaySamples(50).catch(() => null),
      ]);

      if (statsRes?.data?.data) {
        setStats({
          patientsToday: statsRes.data.data.patientsToday ?? 0,
          ordersToday: statsRes.data.data.ordersToday ?? 0,
          samplesCollected: statsRes.data.data.samplesCollected ?? 0,
          resultsCompleted:
            statsRes.data.data.resultsCompleted ??
            statsRes.data.data.resultsReleased ??
            0,
        });
      }

      if (samplesRes?.data?.data && samplesRes.data.data.length > 0) {
        const mapped = samplesRes.data.data.map((item, idx) => ({
          id: item.orderItemId || item._id || item.orderId_raw || `sample-${idx}`,
          orderNumber:
            item.orderId?.orderNumber ||
            item.orderNumber ||
            (typeof item.orderId === "string" ? item.orderId : "") ||
            `ORD-${1000 + idx}`,
          patient: {
            name:
              item.patient?.name ||
              item.orderId?.patientName ||
              item.patientName ||
              (item.patientId?.firstName
                ? `${item.patientId.firstName} ${item.patientId.lastName || ""}`
                : "Patient"),
            patientId:
              item.patient?.mrn ||
              item.patient?.patientId ||
              item.orderId?.patientMrn ||
              item.patientMrn ||
              item.patientId?.patientId ||
              `PT-${100 + idx}`,
          },
          testName:
            item.testName ||
            item.orderItemId?.testName ||
            item.testCatalogId?.testName ||
            "General Lab Test",
          sampleId: item.sampleId && item.sampleId !== "-" ? item.sampleId : item.sampleBarcode || item.specimenId || "—",
          currentLocation: item.currentLocation || item.specimen?.currentLocation || (item.sampleId && item.sampleId !== "—" && item.sampleId !== "-" ? "Collection Room" : null),
          collectedAt: item.collectedAt
            ? new Date(item.collectedAt).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              }) +
              " • " +
              new Date(item.collectedAt).toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
              })
            : "-",
          status: (() => {
            const ordStatus = (item.orderStatus || "").toString().toUpperCase();
            if (["COMPLETED", "RELEASED", "VALIDATED"].includes(ordStatus)) {
              return "Completed";
            }
            const rawStatus = (item.status || item.orderStatus || item.itemStatus || "").toString().toUpperCase();
            if (
              rawStatus.includes("SAMPLE COLLECTED") ||
              rawStatus.includes("SAMPLE_COLLECTED") ||
              rawStatus.includes("COLLECTED") ||
              rawStatus.includes("RECEIVED")
            ) {
              return "Sample Collected";
            }
            if (
              rawStatus.includes("RESULT PENDING") ||
              rawStatus.includes("RESULT_PENDING") ||
              rawStatus.includes("IN_PROCESS") ||
              rawStatus.includes("RESULT_ENTERED") ||
              rawStatus.includes("IN_PROGRESS")
            ) {
              return "Result Pending";
            }
            if (
              rawStatus.includes("COMPLETED") ||
              rawStatus.includes("RELEASED") ||
              rawStatus.includes("VALIDATED")
            ) {
              return "Completed";
            }
            if (rawStatus.includes("CANCELLED") || rawStatus.includes("REJECTED")) {
              return "Cancelled";
            }
            return "Ordered";
          })(),
          raw: item,
        }));
        setSamples(mapped);
      } else {
        setSamples([]);
      }
    } catch (err) {
      console.error("Failed to fetch dashboard data:", err);
      setSamples([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filtered samples based on search query and status filter
  const filteredSamples = useMemo(() => {
    return samples.filter((item) => {
      const q = searchQuery.toLowerCase().trim();
      const matchesSearch =
        !q ||
        item.orderNumber?.toLowerCase().includes(q) ||
        item.patient?.name?.toLowerCase().includes(q) ||
        item.patient?.patientId?.toLowerCase().includes(q) ||
        item.sampleId?.toLowerCase().includes(q) ||
        item.testName?.toLowerCase().includes(q);

      const matchesStatus =
        statusFilter === "all" || item.status.toLowerCase() === statusFilter.toLowerCase();

      return matchesSearch && matchesStatus;
    });
  }, [samples, searchQuery, statusFilter]);

  // Handle action click based on sample status
  const handleActionClick = (sample) => {
    if (sample.status === "Ordered") {
      onCollectSample?.(sample.raw?.orderId_raw || sample.raw?.orderId?._id || sample.raw?.orderId || sample.id);
    } else if (sample.status === "Sample Collected" || sample.status === "Result Pending") {
      onEnterResult?.(
        sample.raw?.orderItemId?._id || sample.raw?.orderItemId,
        sample.raw?.specimenId || sample.raw?._id || sample.id,
        sample.raw?.orderId_raw || sample.raw?.orderId?._id || sample.raw?.orderId
      );
    } else if (sample.status === "Completed") {
      onViewReport?.(sample.raw?.resultId || sample.id);
    }
  };

  return (
    <Box sx={{ width: "100%", pb: 1.5 }}>
      {/* 1. TOP STAT CARDS (4 in a row - compact pills) */}
      <Grid container spacing={1} sx={{ mb: 1.25 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Patients Today"
            subtitle="Registered today"
            value={stats.patientsToday}
            icon={<PeopleAltOutlined />}
            color="#2563EB"
            bgColor="#EFF6FF"
            isDark={isDark}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Orders Today"
            subtitle="Lab orders today"
            value={stats.ordersToday}
            icon={<AssignmentTurnedInOutlined />}
            color="#16A34A"
            bgColor="#F0FDF4"
            isDark={isDark}
            onClick={onNavigateToSamples}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Samples Collected"
            subtitle="Collected today"
            value={stats.samplesCollected}
            icon={<ScienceOutlined />}
            color="#EA580C"
            bgColor="#FFF7ED"
            isDark={isDark}
            onClick={onNavigateToSamples}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Results Released"
            subtitle="Released today"
            value={stats.resultsCompleted}
            icon={<CheckCircleOutline />}
            color="#9333EA"
            bgColor="#FAF5FF"
            isDark={isDark}
            onClick={onNavigateToResults}
          />
        </Grid>
      </Grid>

      {/* 2. QUICK ACTIONS SECTION - compact strip */}
      <Box sx={{ mb: 1.25 }}>
        <Typography
          sx={{
            fontFamily: "'Satoshi', sans-serif",
            fontSize: "0.78rem",
            fontWeight: 700,
            color: textPrimary,
            mb: 0.6,
          }}
        >
          Quick Actions
        </Typography>

        <Grid container spacing={1}>
          {/* Action 1: Register Patient */}
          <Grid item xs={12} sm={4}>
            <QuickActionCard
              title="Register Patient"
              subtitle="Add new patient details"
              icon={<PersonAddOutlined />}
              color="#16A34A"
              borderColor="#22C55E"
              hoverBg="#F0FDF4"
              isDark={isDark}
              onClick={onRegisterPatient}
            />
          </Grid>

          {/* Action 2: Create Order */}
          <Grid item xs={12} sm={4}>
            <QuickActionCard
              title="Create Order"
              subtitle="Create a new lab order"
              icon={<AssignmentOutlined />}
              color="#2563EB"
              borderColor="#3B82F6"
              hoverBg="#EFF6FF"
              isDark={isDark}
              onClick={onCreateOrder}
            />
          </Grid>

          {/* Action 3: Collect Sample */}
          <Grid item xs={12} sm={4}>
            <QuickActionCard
              title="Collect Sample"
              subtitle="Register a new sample"
              icon={<ScienceOutlined />}
              color="#EA580C"
              borderColor="#F97316"
              hoverBg="#FFF7ED"
              isDark={isDark}
              onClick={onNavigateToSamples}
            />
          </Grid>
        </Grid>
      </Box>

      {/* 3. TODAY'S SAMPLES TABLE CARD */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "8px",
          bgcolor: cardBg,
          border: `1px solid ${borderColor}`,
          boxShadow: "none",
          overflow: "hidden",
        }}
      >
        {/* Table Header Bar */}
        <Box
          sx={{
            px: 1.25,
            py: 0.6,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            justifyContent: "space-between",
            gap: 1,
            borderBottom: `1px solid ${borderColor}`,
          }}
        >
          <Typography
            sx={{
              fontFamily: "'Satoshi', sans-serif",
              fontSize: "0.82rem",
              fontWeight: 700,
              color: textPrimary,
            }}
          >
            Today's Samples
          </Typography>

          <Stack direction="row" spacing={1} alignItems="center">
            {/* Search Input */}
            <TextField
              size="small"
              placeholder="Search patient, sample ID, test..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: textSecondary, fontSize: 16 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: "100%", sm: 260 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                  height: 28,
                  fontSize: "0.75rem",
                  fontFamily: "'Satoshi', sans-serif",
                  bgcolor: isDark ? "#141414" : "#FFFFFF",
                  "& fieldset": {
                    borderColor: borderColor,
                  },
                  "&:hover fieldset": {
                    borderColor: "#3B82F6",
                  },
                  "& .MuiOutlinedInput-input": {
                    py: "2px",
                    px: 1,
                  },
                },
              }}
            />

            {/* Filter Button */}
            <Button
              variant="outlined"
              size="small"
              onClick={(e) => setFilterAnchorEl(e.currentTarget)}
              startIcon={<FilterIcon sx={{ fontSize: 15 }} />}
              sx={{
                height: 28,
                borderRadius: "6px",
                borderColor: statusFilter !== "all" ? "#3B82F6" : borderColor,
                color: statusFilter !== "all" ? "#2563EB" : textPrimary,
                bgcolor: statusFilter !== "all" ? (isDark ? "rgba(37,99,235,0.15)" : "#EFF6FF") : "transparent",
                textTransform: "none",
                fontFamily: "'Satoshi', sans-serif",
                fontWeight: 600,
                fontSize: "0.74rem",
                px: 1.2,
                py: 0,
                "&:hover": {
                  borderColor: "#3B82F6",
                  bgcolor: isDark ? "rgba(59,130,246,0.1)" : "#EFF6FF",
                },
              }}
            >
              {statusFilter === "all" ? "Filter" : statusFilter}
            </Button>

            {/* Filter Popover */}
            <Popover
              open={Boolean(filterAnchorEl)}
              anchorEl={filterAnchorEl}
              onClose={() => setFilterAnchorEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                sx: {
                  p: 1.5,
                  width: 200,
                  bgcolor: cardBg,
                  borderRadius: "8px",
                  border: `1px solid ${borderColor}`,
                  boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                },
              }}
            >
              <Typography
                sx={{
                  fontFamily: "'Satoshi', sans-serif",
                  fontWeight: 700,
                  fontSize: "0.78rem",
                  color: textPrimary,
                  mb: 1,
                }}
              >
                Filter by Status
              </Typography>
              {["all", "Ordered", "Sample Collected", "Result Pending", "Completed"].map(
                (st) => (
                  <MenuItem
                    key={st}
                    onClick={() => {
                      setStatusFilter(st);
                      setFilterAnchorEl(null);
                      setPage(0);
                    }}
                    selected={statusFilter === st}
                    sx={{
                      borderRadius: "5px",
                      fontSize: "0.75rem",
                      fontFamily: "'Satoshi', sans-serif",
                      fontWeight: statusFilter === st ? 700 : 500,
                      py: 0.6,
                    }}
                  >
                    {st === "all" ? "All Statuses" : st}
                  </MenuItem>
                )
              )}
            </Popover>
          </Stack>
        </Box>

        {/* Table Content */}
        <TableContainer sx={{ maxHeight: 520 }}>
          <Table size="small" sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: isDark ? "#171717" : "#F8FAFC", height: 28 }}>
                {[
                  "Order ID",
                  "Patient",
                  "Test / Profile",
                  "Sample ID",
                  "Collected At",
                  "Status",
                  "Action",
                ].map((header) => (
                  <TableCell
                    key={header}
                    align={header === "Action" ? "right" : "left"}
                    sx={{
                      fontFamily: "'Satoshi', sans-serif",
                      fontWeight: 700,
                      fontSize: "0.68rem",
                      color: textSecondary,
                      py: "3px !important",
                      px: "8px !important",
                      borderBottom: `1px solid ${borderColor}`,
                      textTransform: "uppercase",
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} sx={{ color: "#2563EB" }} />
                  </TableCell>
                </TableRow>
              ) : filteredSamples.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    align="center"
                    sx={{ py: 4, color: textSecondary, fontFamily: "'Satoshi', sans-serif", fontSize: "0.75rem" }}
                  >
                    No samples found matching your criteria
                  </TableCell>
                </TableRow>
              ) : (
                filteredSamples
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((sample) => (
                    <TableRow
                      key={sample.id || sample.orderNumber}
                      hover
                      sx={{
                        height: 30,
                        "&:last-child td, &:last-child th": { border: 0 },
                        "&:hover": {
                          bgcolor: isDark ? "rgba(255,255,255,0.03)" : "rgba(241,245,249,0.7)",
                        },
                      }}
                    >
                      {/* 1. Order ID */}
                      <TableCell
                        sx={{
                          fontFamily: "'Satoshi', sans-serif",
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          color: "#2563EB",
                          cursor: "pointer",
                          px: "8px !important",
                          py: "2px !important",
                          borderBottom: `1px solid ${borderColor}`,
                          whiteSpace: "nowrap",
                          "&:hover": { textDecoration: "underline" },
                        }}
                        onClick={() => handleActionClick(sample)}
                      >
                        {sample.orderNumber}
                      </TableCell>

                      {/* 2. Patient */}
                      <TableCell
                        sx={{
                          px: "8px !important",
                          py: "2px !important",
                          borderBottom: `1px solid ${borderColor}`,
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: "'Satoshi', sans-serif",
                            fontWeight: 700,
                            fontSize: "0.76rem",
                            color: textPrimary,
                            lineHeight: 1.15,
                          }}
                        >
                          {sample.patient?.name}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "'Satoshi', sans-serif",
                            fontSize: "0.66rem",
                            color: textSecondary,
                            lineHeight: 1,
                          }}
                        >
                          {sample.patient?.patientId}
                        </Typography>
                      </TableCell>

                      {/* 3. Test / Profile */}
                      <TableCell
                        sx={{
                          fontFamily: "'Satoshi', sans-serif",
                          fontWeight: 500,
                          fontSize: "0.74rem",
                          color: textPrimary,
                          px: "8px !important",
                          py: "2px !important",
                          borderBottom: `1px solid ${borderColor}`,
                        }}
                      >
                        {sample.testName}
                      </TableCell>

                      {/* 4. Sample ID */}
                      <TableCell
                        sx={{
                          px: "8px !important",
                          py: "2px !important",
                          borderBottom: `1px solid ${borderColor}`,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                          <Typography
                            sx={{
                              fontFamily: "'Satoshi', sans-serif",
                              fontWeight: 700,
                              fontSize: "0.74rem",
                              color: textPrimary,
                            }}
                          >
                            {sample.sampleId || "—"}
                          </Typography>
                          {sample.sampleId && sample.sampleId !== "—" && sample.sampleId !== "-" && (
                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.2,
                                fontSize: "0.62rem",
                                fontWeight: 600,
                                color: "#059669",
                                bgcolor: isDark ? "rgba(5, 150, 105, 0.15)" : "#ECFDF5",
                                px: 0.5,
                                py: 0.05,
                                borderRadius: "4px",
                                border: "1px solid rgba(5, 150, 105, 0.2)",
                                whiteSpace: "nowrap",
                              }}
                            >
                              📍 {sample.currentLocation || "Collection Room"}
                            </Box>
                          )}
                        </Box>
                      </TableCell>

                      {/* 5. Collected At */}
                      <TableCell
                        sx={{
                          fontFamily: "'Satoshi', sans-serif",
                          fontSize: "0.72rem",
                          color: textSecondary,
                          px: "8px !important",
                          py: "2px !important",
                          borderBottom: `1px solid ${borderColor}`,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {sample.collectedAt ? (
                          sample.collectedAt.includes("•") ? (
                            <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.1 }}>
                              <Box component="span" sx={{ color: textPrimary, fontWeight: 500, fontSize: "0.72rem" }}>
                                {sample.collectedAt.split("•")[0].trim()}
                              </Box>
                              <Box component="span" sx={{ fontSize: "0.64rem", color: textSecondary }}>
                                {sample.collectedAt.split("•")[1]?.trim()}
                              </Box>
                            </Box>
                          ) : (
                            sample.collectedAt
                          )
                        ) : (
                          "—"
                        )}
                      </TableCell>

                      {/* 6. Status */}
                      <TableCell
                        sx={{
                          px: "8px !important",
                          py: "2px !important",
                          borderBottom: `1px solid ${borderColor}`,
                        }}
                      >
                        <StatusBadge status={sample.status} isDark={isDark} />
                      </TableCell>

                      {/* 7. Action Button */}
                      <TableCell
                        align="right"
                        sx={{
                          px: "8px !important",
                          py: "2px !important",
                          borderBottom: `1px solid ${borderColor}`,
                        }}
                      >
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleActionClick(sample)}
                          sx={{
                            borderRadius: "5px",
                            borderColor: "#3B82F6",
                            color: "#2563EB",
                            textTransform: "none",
                            fontFamily: "'Satoshi', sans-serif",
                            fontWeight: 600,
                            fontSize: "0.70rem",
                            px: 1,
                            py: 0,
                            height: 24,
                            whiteSpace: "nowrap",
                            "&:hover": {
                              borderColor: "#1D4ED8",
                              bgcolor: isDark ? "rgba(37,99,235,0.1)" : "#EFF6FF",
                            },
                          }}
                        >
                          {sample.status === "Ordered"
                            ? "Collect Sample"
                            : sample.status === "Completed"
                            ? "View Result"
                            : "Enter Result"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Table Footer with Pagination & View All link */}
        <Box
          sx={{
            px: 1.25,
            py: 0.4,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderTop: `1px solid ${borderColor}`,
            bgcolor: isDark ? "#171717" : "#FFFFFF",
            minHeight: 32,
          }}
        >
          <Typography
            sx={{
              fontFamily: "'Satoshi', sans-serif",
              fontSize: "0.72rem",
              color: textSecondary,
            }}
          >
            {filteredSamples.length === 0
              ? "0 samples"
              : `Showing ${page * rowsPerPage + 1}–${Math.min(
                  (page + 1) * rowsPerPage,
                  filteredSamples.length
                )} of ${filteredSamples.length} samples`}
          </Typography>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <TablePagination
              component="div"
              count={filteredSamples.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 20, 50]}
              sx={{
                border: "none",
                ".MuiTablePagination-toolbar": { minHeight: 28, p: 0 },
                ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                  fontSize: "0.68rem",
                  fontFamily: "'Satoshi', sans-serif",
                  m: 0,
                },
                ".MuiTablePagination-select": {
                  fontSize: "0.68rem",
                  py: "2px",
                  fontFamily: "'Satoshi', sans-serif",
                },
                ".MuiIconButton-root": { p: 0.3 },
              }}
            />

            <Button
              size="small"
              onClick={onNavigateToSamples}
              endIcon={<KeyboardArrowRight sx={{ fontSize: 16 }} />}
              sx={{
                textTransform: "none",
                fontFamily: "'Satoshi', sans-serif",
                fontWeight: 700,
                fontSize: "0.75rem",
                color: "#2563EB",
                p: 0,
                "&:hover": {
                  bgcolor: "transparent",
                  textDecoration: "underline",
                },
              }}
            >
              View all in queue
            </Button>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
}

